import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    InputAdornment,
    IconButton,
    CircularProgress,
    Divider,
    Link as MuiLink,
} from '@mui/material';
import {
    Email,
    Lock,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import TherappyLogo from 'resources/img/therappy-logo.svg';
import { authAPI } from 'resources/api';
import { unsubscribeNotifications } from 'utils/notifications';

// ── Paleta de identidad corporativa ─────────────────────────────────
const BRAND_GREEN = '#B3CC1D';
const BRAND_GREEN_DARK = '#9BAA1A';
const BRAND_GREEN_LIGHT = '#EFF284';
const BRAND_BLACK = '#000000';

const Login = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [serverError, setServerError] = useState('');

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();

    const onSubmit = async ({ email, password }) => {
        setServerError('');
        try {
            const res = await authAPI.login({ email, password });
            localStorage.setItem('auth', JSON.stringify(res));

            const previousUser = localStorage.getItem('userIdentity');
            if (previousUser !== res.identity && process.env.NODE_ENV !== 'development') {
                await unsubscribeNotifications();
            }
            localStorage.setItem('userIdentity', res.identity);

            const userType = res.userType;
            if (userType === 'admin') {
                window.location.href = '/admin';
            } else if (userType === 'business') {
                window.location.href = '/empresas';
            } else {
                window.location.href = '/';
            }
        } catch (err) {
            setServerError('Correo o contraseña incorrectos. Verifica tus datos.');
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#FAFAFA',
                p: 2,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    bgcolor: '#FFFFFF',
                    borderRadius: 3,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                    p: { xs: 3, sm: 5 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                }}
            >
                {/* Logo */}
                <Box sx={{ width: '55%', maxWidth: 200, mt: 1 }}>
                    <img
                        src={TherappyLogo}
                        alt="Terappy"
                        style={{ width: '100%', height: 'auto' }}
                    />
                </Box>

                {/* Título */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontWeight: 700,
                            color: BRAND_BLACK,
                            mb: 0.5,
                        }}
                    >
                        Bienvenido
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: '#666', fontFamily: "'Open Sans', sans-serif" }}
                    >
                        Ayuda psicológica profesional por videollamada
                    </Typography>
                </Box>

                {/* Error */}
                {serverError && (
                    <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
                        {serverError}
                    </Alert>
                )}

                {/* Formulario */}
                <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}
                >
                    <TextField
                        label="Correo electrónico"
                        type="email"
                        required
                        autoComplete="email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        {...register('email', { required: 'El correo es obligatorio' })}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Email sx={{ color: BRAND_GREEN }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_GREEN },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: BRAND_GREEN,
                                    borderWidth: 2,
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: BRAND_GREEN_DARK },
                        }}
                    />

                    <TextField
                        label="Contraseña"
                        type={showPassword ? 'text' : 'password'}
                        required
                        autoComplete="current-password"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        {...register('password', { required: 'La contraseña es obligatoria' })}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock sx={{ color: BRAND_GREEN }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_GREEN },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: BRAND_GREEN,
                                    borderWidth: 2,
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': { color: BRAND_GREEN_DARK },
                        }}
                    />

                    {/* Olvidé mi contraseña */}
                    <Box sx={{ textAlign: 'right', mt: -1 }}>
                        <MuiLink
                            component={Link}
                            to="/recuperar"
                            sx={{
                                color: BRAND_GREEN_DARK,
                                fontSize: '0.85rem',
                                fontFamily: "'Open Sans', sans-serif",
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline', color: BRAND_GREEN },
                            }}
                        >
                            ¿Olvidaste tu contraseña?
                        </MuiLink>
                    </Box>

                    {/* Botón principal */}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                            bgcolor: BRAND_GREEN,
                            color: BRAND_BLACK,
                            fontFamily: "'Open Sans', sans-serif",
                            fontWeight: 700,
                            fontSize: '1rem',
                            borderRadius: '8px',
                            py: 1.5,
                            mt: 1,
                            boxShadow: `0 4px 14px ${BRAND_GREEN}55`,
                            '&:hover': {
                                bgcolor: BRAND_GREEN_DARK,
                                boxShadow: `0 6px 20px ${BRAND_GREEN}77`,
                            },
                            '&.Mui-disabled': {
                                bgcolor: BRAND_GREEN_LIGHT,
                                color: '#666',
                            },
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} sx={{ color: BRAND_BLACK }} />
                        ) : (
                            'Iniciar Sesión'
                        )}
                    </Button>
                </Box>

                {/* Divisor */}
                <Divider sx={{ width: '100%', color: '#ccc' }}>
                    <Typography variant="caption" sx={{ color: '#999', px: 1 }}>
                        ¿No tienes cuenta?
                    </Typography>
                </Divider>

                {/* Links de registro */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Button
                        component={Link}
                        to="/registro"
                        fullWidth
                        variant="outlined"
                        sx={{
                            borderColor: BRAND_GREEN,
                            color: BRAND_GREEN_DARK,
                            fontFamily: "'Open Sans', sans-serif",
                            fontWeight: 600,
                            borderRadius: '8px',
                            py: 1,
                            '&:hover': {
                                borderColor: BRAND_GREEN_DARK,
                                bgcolor: BRAND_GREEN_LIGHT,
                                color: BRAND_BLACK,
                            },
                        }}
                    >
                        Crear cuenta de paciente
                    </Button>
                    <MuiLink
                        component={Link}
                        to="/registro-terapeutas"
                        sx={{
                            color: BRAND_GREEN_DARK,
                            fontFamily: "'Open Sans', sans-serif",
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Registro de psicoterapeutas →
                    </MuiLink>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;
